/**
 * Gyms Page
 *
 * Shows Apex Climbing Gym (the only gym for now) with a "more coming soon" message.
 * Clicking the gym navigates to /climb.
 */

import { useEffect, type CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGym } from '@/hooks/useGyms';
import { useGymStore } from '@/stores/gymStore';

// Default gym ID - Apex is the only gym for now
const DEFAULT_GYM_ID = 'apex-climbing';

/**
 * Gyms page component
 *
 * Shows Apex Climbing Gym (the only gym for now) with a "more coming soon" message.
 */
export function GymsPage() {
  const { gym: apexGym, loading, error } = useGym(DEFAULT_GYM_ID);
  const setActiveGymId = useGymStore((state) => state.setActiveGymId);
  const setLastVisitedPath = useGymStore((state) => state.setLastVisitedPath);
  const navigate = useNavigate();
  const location = useLocation();

  // Track last visited path for returning users
  useEffect(() => {
    setLastVisitedPath(location.pathname);
  }, [location.pathname, setLastVisitedPath]);

  /**
   * Handle gym selection
   */
  const handleGymClick = () => {
    setActiveGymId(DEFAULT_GYM_ID);
    navigate('/climb?view=map');
  };

  // Loading state
  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !apexGym) {
    return (
      <div style={containerStyle}>
        <div style={errorContainerStyle}>
          <h2 style={errorTitleStyle}>Error Loading Gym</h2>
          <p style={errorTextStyle}>{error || 'Gym not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Gyms</h1>

      {/* Apex Gym Card */}
      <div style={gymCardWrapperStyle}>
        <button onClick={handleGymClick} style={gymCardStyle} type="button">
          {/* Gym Logo */}
          <div style={logoContainerStyle}>
            {apexGym.logoURL ? (
              <img src={apexGym.logoURL} alt={`${apexGym.name} logo`} style={logoStyle} />
            ) : (
              <div style={logoPlaceholderStyle}>
                <span style={logoInitialStyle}>{apexGym.name.charAt(0).toUpperCase()}</span>
              </div>
            )}
          </div>

          {/* Gym Info */}
          <div style={infoStyle}>
            <h3 style={nameStyle}>{apexGym.name}</h3>
            {(apexGym.city || apexGym.state) && (
              <p style={locationStyle}>
                {[apexGym.city, apexGym.state].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </button>
      </div>

      {/* More Gyms Coming Soon */}
      <div style={comingSoonStyle}>
        <p style={comingSoonTextStyle}>More gyms coming soon!</p>
        <p style={comingSoonSubtextStyle}>
          We&apos;re working on adding more climbing gyms to Flash.
        </p>
      </div>
    </div>
  );
}

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  padding: '16px 16px 24px 16px',
  height: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
  overflowY: 'auto',
};

const titleStyle: CSSProperties = {
  fontSize: 24,
  fontWeight: 600,
  marginBottom: 16,
  color: 'var(--color-text-primary)',
};

const gymCardWrapperStyle: CSSProperties = {
  marginBottom: 24,
};

const gymCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: 16,
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: 12,
  border: '1px solid var(--color-border)',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
  transition: 'transform 0.2s, box-shadow 0.2s',
};

const logoContainerStyle: CSSProperties = {
  width: 56,
  height: 56,
  flexShrink: 0,
};

const logoStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '50%',
};

const logoPlaceholderStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  backgroundColor: 'var(--color-primary)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const logoInitialStyle: CSSProperties = {
  color: 'white',
  fontSize: '1.5rem',
  fontWeight: 700,
};

const infoStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const nameStyle: CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const locationStyle: CSSProperties = {
  margin: '4px 0 0 0',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 200,
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 200,
  textAlign: 'center',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: 8,
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-error, #ef4444)',
};

const comingSoonStyle: CSSProperties = {
  padding: '24px 16px',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: 12,
  border: '1px dashed var(--color-border)',
  textAlign: 'center',
};

const comingSoonTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const comingSoonSubtextStyle: CSSProperties = {
  margin: '8px 0 0 0',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
};
